// B1.4: move-only from client.ts.
import { ApiClientCore, buildQueryString } from './http/core';

export class ConsumablesApi extends ApiClientCore {
  async actOnConsumableOrder(
    orderId: number,
    action: 'accept' | 'pick' | 'ship' | 'receive' | 'close' | 'cancel',
    payload?: {
      notes?: string;
      lines?: { line_id: number; quantity: number }[];
      confirm_below_book?: boolean;
    }
  ): Promise<any> {
    return this.post(`/api/v1/consumables/orders/${orderId}/${action}`, {
      notes: payload?.notes,
      lines: payload?.lines,
      confirm_below_book: payload?.confirm_below_book ?? false,
    });
  }
  async adjustConsumableInventory(data: {
    location_id: number;
    consumable_type_id: number;
    delta: number;
    reason: string;
    adjustment_kind?: 'inventory_count' | 'write_off' | 'correction';
  }): Promise<any> {
    return this.post('/api/v1/consumables/inventory/adjust', data);
  }
  async approveConsumableRequest(id: number, notes?: string): Promise<any> {
    return this.takeConsumableRequest(id);
  }
  async archiveConsumableSupplyRoute(id: number): Promise<any> {
    return this.post(`/api/v1/consumables/routes/${id}/archive`, {});
  }
  async closeConsumableRequest(
    id: number,
    body: { lines: Array<{ line_id: number; quantity_received: number }>; delivery_notes?: string },
  ): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${id}/close`, body);
  }
  async completeConsumableRequest(id: number, deliveredQuantity?: number, deliveryNotes?: string): Promise<any> {
    const r = await this.getConsumableRequest(id);
    const lines = Array.isArray(r?.lines) ? r.lines : [];
    return this.closeConsumableRequest(id, {
      lines: lines.map((ln: any) => ({
        line_id: ln.id,
        quantity_received:
          deliveredQuantity != null ? deliveredQuantity : Number(ln.quantity_requested) || 0,
      })),
      delivery_notes: deliveryNotes,
    });
  }
  async createConsumableCategory(data: { name: string; sort_order?: number }): Promise<any> {
    return this.post('/api/v1/consumables/categories', data);
  }
  async createConsumableOrderComment(orderId: number, body: string): Promise<any> {
    return this.post(`/api/v1/consumables/orders/${orderId}/comments`, { body });
  }
  async createConsumableOrderFromRequest(requestId: number, data: any): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${requestId}/orders`, data);
  }
  async createConsumableRequest(data: any): Promise<any> {
    return this.post('/api/v1/consumables/request', data);
  }
  async createConsumableRequestComment(requestId: number, body: string): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${requestId}/comments`, { body });
  }
  async createConsumableRequestTemplate(data: any): Promise<any> {
    return this.post('/api/v1/consumables/request-templates', data);
  }
  async createConsumableSupplyRoute(data: Record<string, unknown>): Promise<any> {
    return this.post('/api/v1/consumables/routes', data);
  }
  async createConsumableType(data: any): Promise<any> {
    return this.post('/api/v1/consumables/types', data);
  }
  async deleteConsumableCategory(id: number): Promise<any> {
    return this.delete(`/api/v1/consumables/categories/${id}`);
  }
  async deleteConsumableRequest(id: number): Promise<any> {
    return this.delete(`/api/v1/consumables/requests/${id}`);
  }
  async deleteConsumableRequestTemplate(id: number): Promise<any> {
    return this.delete(`/api/v1/consumables/request-templates/${id}`);
  }
  async deleteConsumableType(id: number): Promise<any> {
    return this.delete(`/api/v1/consumables/types/${id}`);
  }
  async downloadConsumableWorkflowCsv(
    segment:
      | 'summary'
      | 'overdue-requests'
      | 'line-discrepancies'
      | 'route-health'
      | 'top-items'
      | 'top-locations'
      | 'executor-latency'
      | 'sla-breaches',
    params: Record<string, any> = {},
    filename: string
  ): Promise<void> {
    const qs = buildQueryString({ ...params, format: 'csv' });
    const blob = await this.getBlob(`/api/v1/consumables/analytics/workflow/${segment}${qs}`);
    if (typeof window === 'undefined') return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
  async getConsumableCategories(params?: { is_active?: boolean }): Promise<any> {
    return this.get(`/api/v1/consumables/categories${buildQueryString(params || {})}`);
  }
  async getConsumableOrder(id: number): Promise<any> {
    return this.get(`/api/v1/consumables/orders/${id}`);
  }
  async getConsumableOrderComments(orderId: number): Promise<any[]> {
    return this.get(`/api/v1/consumables/orders/${orderId}/comments`);
  }
  async getConsumableOrders(params: Record<string, any> = {}): Promise<any[]> {
    return this.get(`/api/v1/consumables/orders${buildQueryString(params)}`);
  }
  async getConsumableOrdersForRequest(requestId: number): Promise<any[]> {
    return this.get(`/api/v1/consumables/requests/${requestId}/orders`);
  }
  async getConsumableOrgInventorySummary(includeInactive?: boolean): Promise<{ rows: any[] }> {
    return this.get(
      `/api/v1/consumables/inventory/org-summary${buildQueryString({
        include_inactive: includeInactive ?? false,
      })}`,
    );
  }
  async getConsumableRequest(id: number): Promise<any> {
    return this.get(`/api/v1/consumables/requests/${id}`);
  }
  async getConsumableRequestComments(requestId: number): Promise<any[]> {
    return this.get(`/api/v1/consumables/requests/${requestId}/comments`);
  }
  async getConsumableRequestTemplate(id: number): Promise<any> {
    return this.get(`/api/v1/consumables/request-templates/${id}`);
  }
  async getConsumableRequestTemplates(params?: {
    is_active?: boolean;
    location_id?: number;
    supply_route_id?: number;
  }): Promise<any[]> {
    return this.get(`/api/v1/consumables/request-templates${buildQueryString(params || {})}`);
  }
  async getConsumableRequestTemplatesForRoute(
    routeId: number,
    params?: { is_active?: boolean }
  ): Promise<any[]> {
    return this.get(
      `/api/v1/consumables/routes/${routeId}/request-templates${buildQueryString(params || {})}`
    );
  }
  async getConsumableRequests(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/consumables/requests${buildQueryString(params)}`);
  }
  async getConsumableStats(days?: number): Promise<any> {
    return this.get(`/api/v1/consumables/stats${buildQueryString({ from_days_ago: days ?? 30 })}`);
  }
  async getConsumableStockMovements(params: {
    type_id?: number;
    location_id?: number;
    fulfillment_order_id?: number;
    from?: string;
    to?: string;
    limit?: number;
  } = {}): Promise<any> {
    return this.get(`/api/v1/consumables/movements${buildQueryString(params)}`);
  }
  async getConsumableSupplyRoute(id: number): Promise<any> {
    return this.get(`/api/v1/consumables/routes/${id}`);
  }
  async getConsumableTypes(): Promise<any> {
    return this.get('/api/v1/consumables/types');
  }
  async getConsumableWorkflowExecutorLatency(
    params?: { date_from?: string; date_to?: string; min_orders?: number; limit?: number }
  ): Promise<any[]> {
    return this.get(`/api/v1/consumables/analytics/workflow/executor-latency${buildQueryString(params || {})}`);
  }
  async getConsumableWorkflowLineDiscrepancies(
    params?: { date_from?: string; date_to?: string; limit?: number; offset?: number }
  ): Promise<any[]> {
    return this.get(`/api/v1/consumables/analytics/workflow/line-discrepancies${buildQueryString(params || {})}`);
  }
  async getConsumableWorkflowOverdueRequests(params?: { limit?: number; offset?: number }): Promise<any[]> {
    return this.get(`/api/v1/consumables/analytics/workflow/overdue-requests${buildQueryString(params || {})}`);
  }
  async getConsumableWorkflowRouteHealth(
    params?: { date_from?: string; date_to?: string; limit?: number }
  ): Promise<any[]> {
    return this.get(`/api/v1/consumables/analytics/workflow/route-health${buildQueryString(params || {})}`);
  }
  async getConsumableWorkflowSlaBreaches(params?: {
    hours_submitted_stale?: number;
    hours_fo_created_stale?: number;
    hours_fo_accepted_ship_stale?: number;
    limit?: number;
  }): Promise<any[]> {
    return this.get(`/api/v1/consumables/analytics/workflow/sla-breaches${buildQueryString(params || {})}`);
  }
  async getConsumableWorkflowTopItems(params?: { date_from?: string; date_to?: string; limit?: number }): Promise<any[]> {
    return this.get(`/api/v1/consumables/analytics/workflow/top-items${buildQueryString(params || {})}`);
  }
  async listConsumableSupplyRoutes(params?: { include_inactive?: boolean }): Promise<any[]> {
    return this.get(`/api/v1/consumables/routes${buildQueryString(params || {})}`);
  }
  async patchConsumableRequestExternalSupplier(
    requestId: number,
    data: {
      external_order_reference?: string | null;
      external_supplier_status?: 'pending' | 'acknowledged' | 'rejected' | 'cancelled' | null;
      external_supplier_note?: string | null;
    }
  ): Promise<any> {
    return this.patch(`/api/v1/consumables/requests/${requestId}/external-supplier`, data);
  }
  async processConsumableRequest(id: number, notes?: string): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${id}/process`, { notes });
  }
  async receiveConsumableRequest(id: number, notes?: string): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${id}/receive`, { notes });
  }
  async rejectConsumableRequest(id: number, rejectionReason?: string): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${id}/reject`, { rejection_reason: rejectionReason });
  }
  async reorderConsumableTypes(body: { category_id?: number | null; ordered_type_ids: number[] }): Promise<void> {
    await this.put('/api/v1/consumables/types/reorder', body);
  }
  async reportConsumableOrderProblem(orderId: number, body: string): Promise<any> {
    return this.post(`/api/v1/consumables/orders/${orderId}/problems`, { body });
  }
  async reportConsumableRequestProblem(requestId: number, body: string): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${requestId}/problems`, { body });
  }
  async shipConsumableRequest(id: number, notes?: string): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${id}/ship`, { notes });
  }
  async submitConsumableRequestDraft(id: number): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${id}/submit`, {});
  }
  async takeConsumableRequest(id: number): Promise<any> {
    return this.post(`/api/v1/consumables/requests/${id}/take`, {});
  }
  async updateConsumableCategory(
    id: number,
    data: { name?: string; sort_order?: number; is_active?: boolean },
  ): Promise<any> {
    return this.put(`/api/v1/consumables/categories/${id}`, data);
  }
  async updateConsumableRequest(
    id: number,
    data: {
      urgency?: 'low' | 'medium' | 'high';
      notes?: string;
      assigned_to_employee_id?: number | null;
      assigned_to_user_id?: number | null;
      requested_for_employee_id?: number | null;
      requested_for_user_id?: number | null;
      assigned_to_scope_mode?: 'location' | 'all';
      requested_for_scope_mode?: 'location' | 'all';
      requested_deadline_at?: string | null;
      deadline_is_important?: boolean;
      lines?: Array<{ line_id: number; quantity_requested: number }>;
      draft_patch?: Record<string, unknown>;
    }
  ): Promise<any> {
    return this.put(`/api/v1/consumables/requests/${id}`, data);
  }
  async updateConsumableRequestTemplate(id: number, data: any): Promise<any> {
    return this.put(`/api/v1/consumables/request-templates/${id}`, data);
  }
  async updateConsumableSupplyRoute(id: number, data: Record<string, unknown>): Promise<any> {
    return this.put(`/api/v1/consumables/routes/${id}`, data);
  }
  async updateConsumableType(id: number, data: any): Promise<any> {
    return this.put(`/api/v1/consumables/types/${id}`, data);
  }
  async getConsumableRequestEmployees(params: {
    scope_mode: 'location' | 'all';
    location_id?: number;
    search?: string;
    limit?: number;
  }): Promise<any[]> {
    return this.get(`/api/v1/consumables/request-employees${buildQueryString(params || {})}`);
  }
}
